/**
 * `VoucherDocument` — o voucher final (Figma "08 · voucher final — aéreo e
 * hospedagem"), promovido do protótipo validado em `design/src/componentes/voucher`.
 *
 * UM documento para todos os modais: a anatomia é a mesma (identidade → trechos ou
 * blocos do modal → regras → valor → mais informações → procedência) e o que muda é
 * só QUAIS partes a `VoucherDocumentView` traz. Seção sem dado não nasce, e com ela
 * some o divisor — é o que separa "documento enxuto" de "documento quebrado".
 *
 * **O card se adapta à altura do contêiner, sem prop.** Sem altura imposta pelo pai
 * (solto numa página que rola) `max-h-full` não limita nada e o card cresce inteiro.
 * Com altura imposta (drawer, painel lateral) ele para na altura disponível, o MIOLO
 * passa a rolar e a IDENTIDADE e a saia ficam ancoradas fora da área rolável — a
 * mesma anatomia do `SheetContent`, agora nas DUAS pontas (spec do drawer,
 * 2026-08-06: header e rodapé fixos; o conteúdo rola entre eles). Um só caminho de
 * código; o comportamento emerge do contêiner.
 */
import { Fragment, type ReactNode } from 'react';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { TooltipProvider } from '@voll-intelligence/ui-kit/ui/tooltip';
import { cn } from '@voll-intelligence/ui-kit/utils';
import type { VoucherDocumentView } from '../models.js';
import {
  VoucherEnvProvider,
  fitClass,
  type VoucherFit,
  type VoucherHostProps,
} from '../voucher_env.js';
import { VoucherTickets } from './voucher_tickets.js';
import { useVoucherPeople } from '../skeleton/use_voucher_people.js';
import {
  VoucherActions,
  VoucherBlocks,
  VoucherCopyValue,
  VoucherEyebrow,
  VoucherAvatar,
  VoucherMoreInfo,
  VoucherRules,
  VoucherStatusPill,
  VoucherTopActions,
  VoucherTotal,
} from './voucher_parts.js';

export interface VoucherDocumentProps extends VoucherHostProps {
  view: VoucherDocumentView;
  /** Fecha o documento. Opt-in: dentro de um contêiner que já fecha (Sheet,
   *  Dialog), não passe — senão saem dois X na mesma quina. */
  onClose?: () => void;
  /** Abre a conversa já com este voucher no contexto. Botão de largura cheia da saia. */
  onTalkAbout?: () => void;
  /** Copia o resumo do voucher. Ícone de compartilhar no TOPO do documento (share de
   *  mercado); sem handler o ícone não nasce. */
  onShare?: () => void;
  /** Move o voucher para outra viagem. Item do kebab da saia; opt-in. */
  onMoveToTrip?: () => void;
  /** O bloco "Viagem" (#4989) — o `VoucherTripBlock` do intelligence-ui-kit montado pelo
   *  host, como no voucher aéreo: depois do valor, antes do "Mais informações". */
  tripBlock?: ReactNode;
  /** Abre o fluxo de cancelamento determinístico do voucher. Item destrutivo do
   *  kebab da saia. Opt-in: dentro de um contêiner que só LÊ o documento, não passe —
   *  sem handler o item não nasce. */
  onRequestCancel?: () => void;
  /** How the document sits in its container; `card` when absent. The fit is the mounting
   *  screen's decision, not the document's. */
  fit?: VoucherFit;
}

/** O eyebrow e o selo na mesma linha e, logo abaixo do selo e à direita como ele, a
 *  linha de apoio do status ("Em aprovação" → quem aprova) — complemento do selo,
 *  não uma linha da identidade. */
function VoucherEyebrowRow({ view }: Readonly<{ view: VoucherDocumentView }>) {
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <VoucherEyebrow>{view.eyebrow}</VoucherEyebrow>
        {view.status ? (
          <VoucherStatusPill label={view.status.label} tone={view.status.tone} />
        ) : null}
      </div>
      {view.status?.detail ? (
        <p className="text-right text-detail text-muted-foreground">{view.status.detail}</p>
      ) : null}
    </>
  );
}

/** Identidade — o eyebrow com a categoria, o selo de status, o código copiável, a rota
 *  (ou o nome do fornecedor) como herói e a linha de resumo com o viajante. As ações do topo
 *  moram na linha ACIMA desta (`VoucherTopActions`), então eyebrow e selo têm a
 *  largura inteira — no telefone os quatro não cabem numa linha só (#2600). */
function VoucherIdentity({ view }: Readonly<{ view: VoucherDocumentView }>) {
  const people = useVoucherPeople(view);
  return (
    <header className="grid gap-2.5">
      <VoucherEyebrowRow view={view} />
      {/* O código fora do eyebrow em versalete (#7373): legível e copiável, como o endereço. */}
      {view.code ? (
        <div className="text-title text-foreground">
          <VoucherCopyValue value={view.code.value} what={view.code.copy} />
        </div>
      ) : null}
      <div className="grid gap-1">
        <h2 className="text-heading font-medium text-foreground">{view.heading}</h2>
        {view.subheading ? <p className="text-title text-foreground">{view.subheading}</p> : null}
      </div>
      {view.address ? (
        <div className="text-ui font-normal text-muted-foreground">
          <VoucherCopyValue value={view.address.value} what={view.address.copy} />
        </div>
      ) : null}
      {/* Truthiness, e não `!= null`: string vazia é ausência de dado como
          qualquer outra, e a linha inteira tem de sumir em vez de reservar altura
          para nada. */}
      {view.summary || people.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 text-detail text-muted-foreground">
          {view.summary ? (
            <span>{people.length > 0 ? `${view.summary} ·` : view.summary}</span>
          ) : null}
          {/* O viajante e cada acompanhante com o PRÓPRIO avatar — eles dividem a
              reserva, não o nome; concatenar quebraria as iniciais. A key leva o
              índice porque homônimos são dado válido no PNR — nome puro colidiria;
              a lista é estática, então o índice é estável. */}
          {people.map(({ name, photoUrl }, index) => (
            <Fragment key={`${name}-${String(index)}`}>
              <VoucherAvatar name={name} photoUrl={photoUrl} />
              <span>{name}</span>
            </Fragment>
          ))}
        </div>
      ) : null}
    </header>
  );
}

export function VoucherDocument({
  view,
  onClose,
  onTalkAbout,
  onShare,
  onMoveToTrip,
  onRequestCancel,
  tripBlock,
  fit,
  copy,
  writeClipboard,
  onCopyFailed,
  photosFor,
}: Readonly<VoucherDocumentProps>) {
  const tickets = view.tickets ?? [];
  const withTopActions = onClose != null || onShare != null;
  return (
    /* `TooltipProvider` UMA vez, aqui: sem ele nenhum tooltip do documento abre, e
       exigir que cada consumidor lembre de embrulhar é como isso quebra em silêncio.
       O `delay` é o mesmo do mapa de assentos — informação de apoio não pode saltar
       no primeiro pixel. */
    <VoucherEnvProvider env={{ copy, writeClipboard, onCopyFailed, photosFor }}>
      <TooltipProvider delay={120}>
        <article
          className={cn(
            /* `overflow-hidden` por causa do raio: sem ele o conteúdo rolável passa
             por cima dos cantos arredondados. */
            'relative flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-card border border-border bg-card',
            fitClass(fit),
          )}
        >
          {/* A identidade é o HEADER FIXO do documento (spec do drawer, 2026-08-06):
            ela e o cluster de ações ficam FORA da área rolável, então categoria,
            código, selo e o X seguem à vista com o miolo rolando por baixo. O
            cluster é a PRIMEIRA linha do header, e a identidade encosta nele com
            um respiro menor que o `p-6` de quando não há ação nenhuma. */}
          <div className="shrink-0">
            <VoucherTopActions onShare={onShare} onClose={onClose} />
            <div className={cn('px-6 pb-0', withTopActions ? 'pt-2' : 'pt-6')}>
              <VoucherIdentity view={view} />
            </div>
          </div>
          {/* `scroll-fade`: o utilitário do repo (o mesmo do `SidebarContent` e do
            `MessageScroller`) dissolve as duas bordas do miolo — conteúdo cortado
            não termina numa linha reta que parece fim de documento, e no topo ele
            dissolve o que passa por baixo do header fixo. */}
          <div className="scroll-fade scrollbar-rail [scrollbar-gutter:auto]! relative min-h-0 flex-1 overflow-y-auto">
            <div className="space-y-6 p-6">
              {tickets.length > 0 ? (
                <>
                  <Separator />
                  <VoucherTickets tickets={tickets} footnote={view.footnote} />
                </>
              ) : null}
              <VoucherBlocks blocks={view.blocks ?? []} />
              {view.rules && view.rules.rows.length > 0 ? (
                <VoucherRules
                  title={view.rules.title}
                  rows={view.rules.rows}
                  notes={view.rules.notes}
                />
              ) : null}
              {view.total ? (
                <VoucherTotal
                  value={view.total.value}
                  struck={view.total.struck}
                  policy={view.total.policy}
                />
              ) : null}
              {tripBlock}
              <VoucherMoreInfo items={view.more ?? []} />
              {view.provenance ? (
                <p className="text-caption text-muted-foreground">{view.provenance}</p>
              ) : null}
            </div>
          </div>
          {/* Sem `Separator` acima: o `scroll-fade` do miolo já dissolve o conteúdo na
            borda e diz que ali termina o rolável — divisor por cima disso é uma
            segunda linha dizendo a mesma coisa. */}
          <VoucherActions
            onTalkAbout={onTalkAbout}
            onMoveToTrip={onMoveToTrip}
            onRequestCancel={onRequestCancel}
          />
        </article>
      </TooltipProvider>
    </VoucherEnvProvider>
  );
}
